import React from 'react';
import { scaleTime } from 'd3-scale';
import { D3Scale, DomainTuple, VictoryCommonProps } from 'victory-core';

import { Datum, ResultChartComponent } from './ResultChartComponent';

type Props = Omit<VictoryCommonProps, 'scale'> & {
  selectedResourceId?: string;
  data: Datum[];
  initialZoomDomain: {
    x: DomainTuple;
    y: DomainTuple;
  };
  onClick: (datum: Datum) => void;
  scale?: {
    x: D3Scale;
    y: D3Scale;
  };
};

export const CustomScatterChart = React.memo((props: Props) => {
  const initialDomain = props.initialZoomDomain.x;

  if (
    props.width === undefined ||
    props.height === undefined ||
    props.scale === undefined ||
    typeof props.padding !== 'object' ||
    props.padding.left === undefined ||
    props.padding.right === undefined ||
    props.padding.bottom === undefined ||
    props.padding.top === undefined
  ) {
    return null;
  }

  const scale = props.scale;
  const areaWidth = props.width - props.padding.left - props.padding.right;
  const areaHeight = props.height - props.padding.top - props.padding.bottom;
  const visibleScale = scaleTime().domain(initialDomain).range([0, 1]);
  const pointsToAnimate = props.data.filter(
    datum => visibleScale(datum.x) >= 0 && visibleScale(datum.x) <= 1,
  ).length;

  let animationIndex = -1;
  const getAnimationIndex = (datum: Datum) => {
    const isVisible = visibleScale(datum.x) >= 0 && visibleScale(datum.x) <= 1;

    return isVisible ? animationIndex++ : pointsToAnimate;
  };

  return (
    <>
      <defs>
        <clipPath id="viewPort">
          <rect
            x={props.padding.left}
            y={props.padding.top}
            width={areaWidth}
            height={areaHeight}
          />
        </clipPath>
      </defs>
      <g clipPath="url(#viewPort)">
        {props.data?.map((datum, index) => (
          <ResultChartComponent
            key={datum.id ?? index}
            selected={props.selectedResourceId === datum.id}
            animationIndex={getAnimationIndex(datum)}
            onClick={props.onClick}
            x={scale.x(datum.x)}
            y={scale.y(Number(datum.y))}
            datum={datum}
            showValue={
              props.selectedResourceId === datum.id || props.data.length < 10
            }
          />
        ))}
      </g>
    </>
  );
});
